import React, { useContext } from "react";
import styled from "styled-components";

import {
	RootProductId,
	ModuleSetting,
	NewModuleSetting,
	getRootProductDescription,
	CongratsSettings,
} from "@models";
import { formatDate } from "../../../components/forms/datePickerUtils";
import ModuleSettingAmountsSummary from "./ModuleSettingAmountsSummary";
import {
	Badges,
	FlexCol,
	Icon,
	IconButton,
	LargeText,
	SmallText,
	Tooltip,
} from "@components";
import { isNewModuleSetting } from "./util";
import { getDateFlags } from "@util";
import CongratsHeader from "./congrats/congratsHeader";
import ModuleSettingControlsDropdown from "./ModuleSettingControlsDropdown";
import UserDetailsPageContext from "../../user/UserDetailsPageContext";
import { CongratsOccasions } from "@models";
import { addDays } from "date-fns";
import { useProduct } from "@hooks";

type ModuleSettingCardHeaderProps = {
	moduleSetting: ModuleSetting | NewModuleSetting;
	rootProductId: RootProductId;
	onCopy: () => void;
};

export function ModuleSettingCardHeader(props: ModuleSettingCardHeaderProps) {
	const { moduleSetting, rootProductId, onCopy } = props;

	const product = useProduct(rootProductId);
	const showAmounts = product.hasBudgets();
	const { isExpired, isInFuture } = getDateFlags(
		moduleSetting.availableFrom,
		moduleSetting.availableUntil
	);

	const handleCopy = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		onCopy();
	};

	const isNew = isNewModuleSetting(moduleSetting);

	const { user } = useContext(UserDetailsPageContext);
	const balanceAccount = user.balanceAccounts.find(
		(ba) => ba.balanceAccountId === moduleSetting.balanceAccountId
	);

	return (
		<>
			<FlexCol
				css={`
					min-width: 4em;
				`}
			>
				{showAmounts && (
					<ModuleSettingAmountsSummary moduleSetting={moduleSetting} />
				)}
			</FlexCol>
			<FlexCol
				css={`
					flex: 0 1 20em;
				`}
			>
				{moduleSetting.category === "congrats" ? (
					<CongratsHeader moduleSetting={moduleSetting} />
				) : (
					<LargeText>{getRootProductDescription(rootProductId).name}</LargeText>
				)}
			</FlexCol>
			<FlexCol>
				<AvailabilityDates moduleSetting={moduleSetting} />
			</FlexCol>
			{isExpired && <Badges.Expired />}
			{isInFuture && <Badges.InFuture />}
			<Spacer />
			{!!balanceAccount && (
				<Tooltip id="tooltip-icon-button" tooltip={"Guthabenkonto aktiv"}>
					<Icon.BalanceAccount />
				</Tooltip>
			)}
			{!isNew && (
				<CopyButton onClick={handleCopy} tooltip="Moduleinstellung kopieren">
					<Icon.Copy />
				</CopyButton>
			)}
			{isNew && <Badges.New />}
			{"moduleSettingId" in moduleSetting && (
				<ModuleSettingControlsDropdown
					moduleSetting={moduleSetting as ModuleSetting}
				/>
			)}
		</>
	);
}

function AvailabilityDates(props: {
	moduleSetting: ModuleSetting | NewModuleSetting;
}) {
	const { moduleSetting } = props;
	let endDate = moduleSetting.availableUntil;
	const startDate = moduleSetting.availableFrom;
	const congratsSettings =
		moduleSetting.category === "congrats"
			? (moduleSetting.settings as CongratsSettings)
			: null;

	if (
		!endDate &&
		congratsSettings &&
		congratsSettings.occasion !== CongratsOccasions.Birthday
	) {
		endDate = addDays(startDate, congratsSettings.validTimeInDays - 1);
	}

	return (
		<>
			<SmallText>
				<span title="Verfügbar ab">{formatDate(startDate)}</span>
				{"-"}
			</SmallText>
			<SmallText title="Verfügbar bis">
				{endDate ? formatDate(endDate) : "unbefristet"}
			</SmallText>
		</>
	);
}

const CopyButton = styled(IconButton)`
	background-color: transparent;
	padding: 5px;
	width: fit-content;
	justify-self: end;

	& > * {
		margin: 0;
	}
`;

const Spacer = styled.div`
	flex: 1 0 auto;
`;
